import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthCard from '../components/AuthCard';
import SocialButtons from '../components/SocialButtons';
import ErrorBox from '../components/ErrorBox';
import EyeIcon from '../components/EyeIcon';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch('https://membify.dk/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        if (data.requires_2fa) {
          navigate('/2fa', { state: { email, token: data.token } });
        } else {
          window.location.href = data.redirect || '/';
        }
        return;
      }

      setError(mapError(res.status, data));
    } catch {
      setError({
        title: 'Ingen forbindelse',
        message: "Vi kunne ikke nå Membify's servere. Tjek din internetforbindelse og prøv igen.",
        type: 'network',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthCard>

      <SocialButtons label="Log ind med" />

      <form className="login-form" onSubmit={handleSubmit} noValidate>
        {error && <ErrorBox error={error} />}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="din@email.dk"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Adgangskode</label>
          <div className="input-with-toggle">
            <input
              id="password"
              type={showPass ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="toggle-pass"
              onClick={() => setShowPass((v) => !v)}
              aria-label={showPass ? 'Skjul adgangskode' : 'Vis adgangskode'}
              tabIndex={-1}
            >
              <EyeIcon open={showPass} />
            </button>
          </div>
        </div>

        <div className="login-options">
          <label className="remember-me">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>Husk mig</span>
          </label>
          <button type="button" className="forgot-link" onClick={() => navigate('/forgot')}>
            Glemt adgangskode?
          </button>
        </div>

        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? <span className="btn-spinner" /> : 'Log ind'}
        </button>
      </form>

      <p className="login-footer">
        Har du ikke en konto?{' '}
        <button className="link-btn" onClick={() => navigate('/signup')}>Opret konto</button>
      </p>
    </AuthCard>
  );
}

function mapError(status, data) {
  if (status === 401) {
    return {
      title: 'Forkert email eller adgangskode',
      message: 'Tjek at du har skrevet korrekt og prøv igen. Har du glemt din adgangskode?',
      type: 'auth',
      action: { label: 'Nulstil adgangskode', href: '#/forgot' },
    };
  }
  if (status === 403) {
    return {
      title: 'Konto spærret',
      message: data.message || 'Din konto er midlertidigt spærret på grund af for mange fejlede forsøg. Prøv igen om 15 minutter eller kontakt support.',
      type: 'blocked',
      support: true,
    };
  }
  if (status === 404) {
    return {
      title: 'Ingen konto med denne email',
      message: 'Vi kunne ikke finde en konto med denne email. Har du måske brugt en anden?',
      type: 'notfound',
      action: { label: 'Opret konto', href: '#/signup' },
    };
  }
  if (status === 429) {
    return {
      title: 'For mange forsøg',
      message: 'Du har prøvet for mange gange på kort tid. Vent et øjeblik og prøv igen.',
      type: 'ratelimit',
    };
  }
  if (status >= 500) {
    return {
      title: 'Noget gik galt hos os',
      message: 'Der er et midlertidigt problem på vores servere. Vi er klar over det — prøv igen om lidt.',
      type: 'server',
      support: true,
    };
  }
  return {
    title: 'Ukendt fejl',
    message: data.message || 'Noget gik galt. Prøv igen eller kontakt support.',
    type: 'unknown',
    support: true,
  };
}
